/**
 * IntegratedSidebar Component
 * 
 * A responsive sidebar that adapts its behavior based on viewport:
 * - Desktop: Slides from left side
 * - Mobile: Slides up from bottom as a sheet
 * 
 * Provides contextual information and navigation for the UnifiedResultsModal
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaGripLines, FaChevronUp } from 'react-icons/fa';
import CompanionJobList from '../companion/views/CompanionJobList';
import CompanionEvidenceDetails from '../companion/views/CompanionEvidenceDetails';
import CompanionOrderList from '../companion/views/CompanionOrderList';
import CompanionOwnerControls from '../companion/views/CompanionOwnerControls';
import './integrated-sidebar.css';

const IntegratedSidebar = ({
  isOpen = false,
  onToggle,
  currentView = null,
  context = null,
  isMobile = false,
  className = '',
  children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragStartY, setDragStartY] = useState(null);
  const [sheetHeight, setSheetHeight] = useState(85); // Percentage for mobile - increased default height
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      if (mobile !== isMobile && onToggle) {
        onToggle(false); // Close when switching between mobile/desktop
      }
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile, onToggle]);

  // Handle expand/collapse with animation
  const handleToggle = useCallback(() => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    const newState = !isExpanded;
    setIsExpanded(newState);
    
    setTimeout(() => {
      setIsAnimating(false);
      if (onToggle) {
        onToggle(newState);
      }
    }, 300);
  }, [isExpanded, isAnimating, onToggle]);

  // Mobile bottom sheet drag handling
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setDragStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !dragStartY) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = dragStartY - currentY;
    const viewportHeight = window.innerHeight;
    const deltaPercent = (deltaY / viewportHeight) * 100;
    
    // Update sheet height while dragging - allow larger range for better usability
    const newHeight = Math.min(95, Math.max(30, sheetHeight + deltaPercent));
    setSheetHeight(newHeight);
  };

  const handleTouchEnd = (e) => {
    if (!isMobile || !dragStartY) return;
    
    const endY = e.changedTouches[0].clientY;
    const deltaY = dragStartY - endY;
    
    // Snap points for the bottom sheet
    if (deltaY > 50) {
      // Swiped up - expand to 75%
      setSheetHeight(75);
      setIsExpanded(true);
    } else if (deltaY < -50) {
      // Swiped down - collapse or close
      if (sheetHeight > 50) {
        setSheetHeight(25);
      } else {
        handleToggle();
      }
    }
    
    setDragStartY(null);
  };

  // Determine which content to render based on context
  const renderContent = () => {
    if (children) {
      return children;
    }

    if (!context || !context.type) {
      return (
        <div className="sidebar-empty-state">
          <p>No contextual information available</p>
        </div>
      );
    }

    switch (context.type) {
      case 'owner-controls':
        // Owner controls panel - shows edit title, privacy, and evidence info
        return <CompanionOwnerControls data={context.data} />;
      
      case 'job-list':
        return <CompanionJobList data={context.data} />;
      
      case 'evidence-details':
        return <CompanionEvidenceDetails data={context.data} />;
      
      case 'order-list':
        return <CompanionOrderList data={context.data} />;
      
      case 'similar-jobs':
        return (
          <div className="sidebar-similar-jobs">
            <h3>Navigation History</h3>
            <div className="navigation-breadcrumbs">
              {context.data?.navigationPath?.map((item, index) => (
                <div key={index} className="breadcrumb-item">
                  <span className="breadcrumb-label">{item.label}</span>
                  {item.jobId && (
                    <span className="breadcrumb-id">#{item.jobId}</span>
                  )}
                </div>
              ))}
            </div>
            <CompanionJobList data={context.data} />
          </div>
        );
      
      case 'filters':
        return (
          <div className="sidebar-filters">
            <h3>Filters & Options</h3>
            {context.data?.filters}
          </div>
        );
      
      default:
        return (
          <div className="sidebar-custom-content">
            {context.data?.content}
          </div>
        );
    }
  };

  // Desktop sidebar tab
  const renderDesktopTab = () => (
    <button
      className={`sidebar-tab desktop ${isExpanded ? 'active' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggle();
      }}
      aria-label="Toggle sidebar"
      aria-expanded={isExpanded}
    >
      <span className="tab-icon">
        {isExpanded ? <FaChevronLeft /> : <FaChevronRight />}
      </span>
      <span className="tab-label">Control</span>
      {context?.notificationCount > 0 && (
        <span className="tab-badge">{context.notificationCount}</span>
      )}
    </button>
  );

  // Mobile bottom sheet handle
  const renderMobileHandle = () => (
    <div 
      className="mobile-sheet-handle"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="handle-bar">
        <FaGripLines />
      </div>
      <div className="handle-text">
        {isExpanded ? 'Swipe down to minimize' : 'Swipe up for more'}
      </div>
    </div>
  );

  // Mobile tab for when sidebar is closed
  const renderMobileTab = () => (
    <button
      className="sidebar-tab mobile"
      onClick={handleToggle}
      onTouchStart={(e) => {
        e.preventDefault();
        handleToggle();
      }}
      aria-label="Open sidebar"
      title={`Mobile: ${isMobile}, Expanded: ${isExpanded}, Open: ${isOpen}`} // Debug info
    >
      <div className="tab-content">
        <div className="tab-icon">
          <FaChevronUp />
        </div>
        <div className="tab-label">
          {context?.title || 'Details'}
        </div>
      </div>
      {context?.badge && (
        <span className="tab-badge">{context.badge}</span>
      )}
    </button>
  );

  // Show tab when sidebar is not expanded (mobile) or not open (desktop)
  if (!isExpanded && isMobile) {
    return renderMobileTab();
  }
  
  if (!isOpen && !isExpanded) {
    return isMobile ? renderMobileTab() : renderDesktopTab();
  }

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isExpanded && (
        <div 
          className="sidebar-backdrop mobile"
          onClick={handleToggle}
          aria-hidden="true"
        />
      )}

      {/* Main sidebar container */}
      <aside
        ref={sidebarRef}
        className={`
          integrated-sidebar 
          ${className}
          ${isExpanded ? 'expanded' : 'collapsed'}
          ${isAnimating ? 'animating' : ''}
          ${isMobile ? 'mobile-sheet' : 'desktop-panel'}
        `}
        style={isMobile ? { height: `${sheetHeight}vh` } : {}}
        aria-label="Contextual sidebar"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Desktop tab or mobile handle */}
        {isMobile ? renderMobileHandle() : renderDesktopTab()}

        {/* Sidebar content */}
        <div 
          ref={contentRef}
          className="sidebar-content"
        >
          {/* Header */}
          <div className="sidebar-header">
            <h2 className="sidebar-title">
              {context?.title || 'Details'}
            </h2>
            <button
              className="sidebar-close"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggle();
              }}
              aria-label="Close sidebar"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="sidebar-body">
            {renderContent()}
          </div>

          {/* Footer (optional) */}
          {context?.actions && context.actions.length > 0 && (
            <div className="sidebar-footer">
              {context.actions.map((action, index) => (
                <button
                  key={index}
                  className="sidebar-action"
                  onClick={action.onClick}
                >
                  {action.icon && <span className="action-icon">{action.icon}</span>}
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default IntegratedSidebar;
